/**
 * The main diagnosis page.
 *
 * Notable behaviours:
 *   - detection runs locally for an instant hint, but the server re-detects and
 *     is authoritative
 *   - redaction is previewed locally so the user can see what will be stored
 *   - the AI key stays in component state only and is cleared when the page
 *     unmounts or the session ends
 *   - the idempotency key is created once per submission and reused on retry
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { INPUT_TYPES, type InputTypeSelection } from '@core/types';
import type { Entitlements, QuotaStatus } from '@core/types';
import { detectInputType } from '@core/input-detect';
import { redactSecrets, normaliseCustomFields } from '@core/redact';
import { newIdempotencyKey } from '@core/ids';
import { DEFAULT_BASE_URL } from '@core/providers';
import { Alert, Badge, CodeBlock, Field, QuotaMeter, Spinner } from '../components/ui';
import { PageHead } from '../components/Layout';
import { api, ApiError, type DiagnosisResponse, type SpecSummary } from '../lib/api';
import { useAsync } from '../lib/hooks';
import { formatBytes, formatTitleCase } from '../lib/format';
import { useRouter } from '../lib/router';
import { useSession } from '../lib/session';

export interface NewDiagnosisProps {
  entitlements: Entitlements;
  quota: QuotaStatus;
  onQuotaChange: (quota: QuotaStatus) => void;
}

interface ProviderChoice {
  requested: 'auto' | 'mock' | 'openai-compatible';
  label: string;
  description: string;
}

const PROVIDER_CHOICES: ProviderChoice[] = [
  {
    requested: 'auto',
    label: 'Automatic',
    description:
      'Use a bring-your-own key if you provided one, otherwise the platform provider if enabled, otherwise deterministic analysis only.',
  },
  {
    requested: 'mock',
    label: 'Deterministic mock provider',
    description:
      'No external API is contacted. Useful for demos, offline work and verifying that the pipeline behaves as expected.',
  },
  {
    requested: 'openai-compatible',
    label: 'My OpenAI-compatible key',
    description:
      'Use the key you enter below for this diagnosis only. The key is never stored and never logged.',
  },
];

const PROGRESS_STAGES = [
  'Checking plan and quota',
  'Redacting secrets',
  'Parsing the submission',
  'Validating against OpenAPI',
  'Running deterministic checks',
  'Composing the report',
];

export function NewDiagnosisPage({ entitlements, quota, onQuotaChange }: NewDiagnosisProps) {
  const { getToken, status } = useSession();
  const { navigate } = useRouter();

  const [input, setInput] = useState('');
  const [inputType, setInputType] = useState<InputTypeSelection>('auto');
  const [openApiSpecId, setOpenApiSpecId] = useState<string>('');
  const [customFieldsText, setCustomFieldsText] = useState('');
  const [providerChoice, setProviderChoice] = useState<ProviderChoice['requested']>('auto');
  const [aiKey, setAiKey] = useState('');
  const [aiBaseUrl, setAiBaseUrl] = useState('');
  const [aiModel, setAiModel] = useState('');
  const [rememberKeyWarning, setRememberKeyWarning] = useState(true);
  const [showSanitized, setShowSanitized] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progressIndex, setProgressIndex] = useState(0);
  const [error, setError] = useState<ApiError | Error | null>(null);
  const [notices, setNotices] = useState<string[]>([]);
  const idempotencyRef = useRef<string>(newIdempotencyKey());
  const abortRef = useRef<AbortController | null>(null);

  const specs = useAsync(async (token) => api.listSpecs(token), []);

  const detection = useMemo(() => detectInputType(input), [input]);
  const customFields = useMemo(
    () => normaliseCustomFields(customFieldsText.split(/[,\n]/)),
    [customFieldsText],
  );
  const preview = useMemo(
    () => (input.length > 0 ? redactSecrets(input, { customSensitiveFields: customFields }) : null),
    [input, customFields],
  );

  const inputBytes = useMemo(() => new TextEncoder().encode(input).length, [input]);
  const overLimit = inputBytes > entitlements.maxInputBytes;
  const remaining = quota.remaining;
  const quotaExhausted = remaining <= 0;

  useEffect(() => {
    if (!busy) return;
    const timer = window.setInterval(() => {
      setProgressIndex((value) => Math.min(value + 1, PROGRESS_STAGES.length - 1));
    }, 450);
    return () => window.clearInterval(timer);
  }, [busy]);

  // Clear the key material when the component goes away.
  useEffect(() => () => setAiKey(''), []);

  async function submit() {
    setError(null);
    setNotices([]);
    setBusy(true);
    setProgressIndex(0);
    idempotencyRef.current = newIdempotencyKey();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = await getToken();
      const useKey =
        providerChoice === 'openai-compatible' || (providerChoice === 'auto' && aiKey.length > 0);
      const response: DiagnosisResponse = await api
        .createDiagnosis(
          token,
          {
            input,
            inputType,
            openApiSpecId: openApiSpecId.length > 0 ? openApiSpecId : null,
            customSensitiveFields: customFields,
          },
          {
            idempotencyKey: idempotencyRef.current,
            ...(useKey && aiKey.length > 0
              ? {
                  aiKey: {
                    apiKey: aiKey,
                    ...(aiBaseUrl.trim() ? { baseUrl: aiBaseUrl.trim() } : {}),
                    ...(aiModel.trim() ? { model: aiModel.trim() } : {}),
                  },
                }
              : {}),
            signal: controller.signal,
          },
        )
        .finally(() => {
          // The key is no longer needed once the request has completed.
          if (providerChoice !== 'openai-compatible') setAiKey('');
        });

      onQuotaChange(response.quota);
      if (response.notices.length > 0) setNotices(response.notices);
      navigate(`/app/diagnoses/${response.diagnosis.id}`);
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === 'AbortError') {
        setNotices(['The diagnosis was cancelled before it finished. No quota unit was consumed.']);
      } else {
        setError(
          caught instanceof Error ? caught : new Error('The diagnosis could not be started.'),
        );
      }
      // Refresh the quota so a server-side rejection is reflected immediately.
      try {
        const token = await getToken();
        const me = await api.me(token);
        onQuotaChange(me.quota);
      } catch {
        // ignore refresh failures
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  }

  const canSubmit = !busy && input.trim().length > 0 && !overLimit && !quotaExhausted;

  return (
    <>
      <PageHead
        title="New diagnosis"
        description="Paste a failing request, response, payload or error. Detection, redaction and every deterministic check happen on the server before anything is saved."
        actions={<QuotaMeter quota={quota} />}
      />

      {quotaExhausted ? (
        <Alert tone="warning" title="Monthly quota reached">
          <p>
            You have used all {quota.limit} diagnoses included in the {entitlements.displayName}{' '}
            plan for this month. The counter resets at the start of the next calendar month (UTC).
          </p>
          {entitlements.planId === 'free' ? (
            <button
              type="button"
              className="button--small"
              onClick={() => navigate('/app/billing')}
            >
              See upgrade options
            </button>
          ) : null}
        </Alert>
      ) : null}

      {error ? (
        <Alert tone="error" title="The diagnosis could not be completed">
          <p>{error.message}</p>
          {error instanceof ApiError && error.hint ? <p className="small">{error.hint}</p> : null}
          {error instanceof ApiError && error.requestId ? (
            <p className="small mono faint">Request id: {error.requestId}</p>
          ) : null}
        </Alert>
      ) : null}

      {notices.length > 0 ? (
        <Alert tone="info" title="Notes about this diagnosis">
          <ul className="plain-list">
            {notices.map((notice) => (
              <li key={notice}>{notice}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <div className="card">
        <Field
          label="What failed?"
          htmlFor="diagnosis-input"
          help={
            <>
              {formatBytes(inputBytes)} of {formatBytes(entitlements.maxInputBytes)} allowed on the{' '}
              {entitlements.displayName} plan.
              {input.length === 0
                ? ' Raw curl commands and full HTTP responses produce the best results.'
                : null}
            </>
          }
        >
          <textarea
            id="diagnosis-input"
            className="paste-area"
            value={input}
            spellCheck={false}
            autoComplete="off"
            onChange={(event) => setInput(event.target.value)}
            placeholder={`curl -X POST https://api.example.com/v1/orders \\
  -H 'Authorization: Bearer <token>' \\
  -H 'Content-Type: application/json' \\
  --data-raw '{"amount":100}'`}
            aria-describedby="diagnosis-input-help"
          />
        </Field>

        {overLimit ? (
          <Alert tone="error" title="Input is too large for this plan">
            The submission is {formatBytes(inputBytes)}, which exceeds the{' '}
            {formatBytes(entitlements.maxInputBytes)} limit on the {entitlements.displayName} plan.
            Trim it to the failing request and response, or upgrade.
          </Alert>
        ) : null}

        <div className="grid grid--2" style={{ marginTop: 16 }}>
          <Field
            label="Input type"
            htmlFor="diagnosis-type"
            help={
              inputType === 'auto'
                ? `Automatic detection suggests ${formatTitleCase(detection.type)} (score ${detection.confidence}). ${
                    detection.signals[0] ?? ''
                  }`
                : 'Manual override. The server still records what automatic detection would have chosen.'
            }
          >
            <select
              id="diagnosis-type"
              value={inputType}
              onChange={(event) => setInputType(event.target.value as InputTypeSelection)}
            >
              <option value="auto">Detect automatically</option>
              {INPUT_TYPES.filter((type) => type !== 'unknown').map((type) => (
                <option key={type} value={type}>
                  {formatTitleCase(type)}
                </option>
              ))}
            </select>
          </Field>

          <Field
            label="OpenAPI specification (optional)"
            htmlFor="diagnosis-spec"
            help={
              entitlements.openApiValidation === 'full'
                ? 'Matching operations are validated in full, including request and response schemas.'
                : 'Your plan runs structural checks and operation matching. Full request and response validation is a Pro feature.'
            }
          >
            <select
              id="diagnosis-spec"
              value={openApiSpecId}
              onChange={(event) => setOpenApiSpecId(event.target.value)}
            >
              <option value="">Do not validate against a specification</option>
              {(specs.data?.specs ?? []).map((spec: SpecSummary) => (
                <option key={spec.id} value={spec.id}>
                  {spec.name} ({formatTitleCase(spec.sourceFormat)})
                </option>
              ))}
            </select>
          </Field>
        </div>

        {inputType !== 'auto' && inputType !== detection.type ? (
          <Alert tone="warning">
            You selected {formatTitleCase(inputType)} but automatic detection suggested{' '}
            {formatTitleCase(detection.type)}. The manual choice is used for parsing, and the
            disagreement is recorded in the report.
          </Alert>
        ) : null}

        <div className="row row--between" style={{ marginTop: 16 }}>
          <div className="row">
            <button
              type="button"
              className="button button--primary"
              disabled={!canSubmit}
              onClick={submit}
              aria-busy={busy}
            >
              {busy ? (
                <>
                  <Spinner /> Diagnosing…
                </>
              ) : (
                'Diagnose'
              )}
            </button>
            {busy ? (
              <button type="button" className="button" onClick={() => abortRef.current?.abort()}>
                Cancel
              </button>
            ) : null}
          </div>
          <div className="row small faint">
            <Badge>{remaining} left this month</Badge>
            {input.length > 0 ? <span>{input.length.toLocaleString()} characters</span> : null}
          </div>
        </div>

        {busy ? (
          <div
            className="progress-steps"
            style={{ marginTop: 16 }}
            role="status"
            aria-live="polite"
          >
            {PROGRESS_STAGES.map((stage, index) => (
              <span key={stage} data-active={index <= progressIndex}>
                {index <= progressIndex ? '●' : '○'} {stage}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {preview ? (
        <div className="card">
          <div className="card__header">
            <h2 style={{ margin: 0 }}>Secret redaction preview</h2>
            <button
              type="button"
              className="button--small"
              onClick={() => setShowSanitized((value) => !value)}
              aria-expanded={showSanitized}
            >
              {showSanitized ? 'Hide preview' : 'Show preview'}
            </button>
          </div>

          <Alert tone="warning" title="Review before you submit">
            {preview.report.warning}
          </Alert>

          <div className="row small" style={{ marginTop: 12, gap: 8 }}>
            {preview.report.totalRedactions === 0 ? (
              <Badge>No secrets detected</Badge>
            ) : (
              preview.report.hits.map((hit) => (
                <Badge key={`${hit.placeholder}-${hit.category}`}>
                  {hit.placeholder} × {hit.count}
                </Badge>
              ))
            )}
          </div>

          {showSanitized ? (
            <div style={{ marginTop: 12 }}>
              <CodeBlock
                title="Sanitized text (this is what the server would analyse)"
                code={preview.text}
                maxHeight={360}
              />
            </div>
          ) : null}

          <p className="small faint" style={{ marginTop: 12 }}>
            The server repeats this redaction on the raw text before anything is stored or sent to a
            provider. A local preview is never trusted on its own.
          </p>
        </div>
      ) : null}

      <div className="card">
        <h2>Analysis options</h2>

        <Field
          label="Analysis provider"
          htmlFor="diagnosis-provider"
          help={PROVIDER_CHOICES.find((choice) => choice.requested === providerChoice)?.description}
        >
          <select
            id="diagnosis-provider"
            value={providerChoice}
            onChange={(event) =>
              setProviderChoice(event.target.value as ProviderChoice['requested'])
            }
          >
            {PROVIDER_CHOICES.map((choice) => (
              <option key={choice.requested} value={choice.requested}>
                {choice.label}
              </option>
            ))}
          </select>
        </Field>

        {providerChoice === 'openai-compatible' ||
        (providerChoice === 'auto' && aiKey.length > 0) ? (
          <div className="stack stack--tight" style={{ marginBottom: 16 }}>
            <Alert tone="info" title="How your key is handled">
              <ul className="plain-list">
                <li>It is kept in this browser tab's memory for your active session only.</li>
                <li>It is sent over HTTPS to this application's protected diagnosis endpoint.</li>
                <li>It is used for the diagnosis you request and for nothing else.</li>
                <li>
                  It is never written to the database, never logged and never shown in errors.
                </li>
                <li>It is cleared from memory when you leave this page.</li>
              </ul>
            </Alert>

            <Field
              label="API key"
              htmlFor="ai-key"
              help="Never stored. Cleared when you leave this page."
            >
              <input
                id="ai-key"
                type="password"
                value={aiKey}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setAiKey(event.target.value)}
                placeholder="sk-..."
              />
            </Field>

            <div className="grid grid--2">
              <Field
                label="Base URL (optional)"
                htmlFor="ai-base-url"
                help={`Defaults to ${DEFAULT_BASE_URL}`}
              >
                <input
                  id="ai-base-url"
                  type="url"
                  value={aiBaseUrl}
                  onChange={(event) => setAiBaseUrl(event.target.value)}
                  placeholder={DEFAULT_BASE_URL}
                />
              </Field>
              <Field label="Model (optional)" htmlFor="ai-model" help="Defaults to gpt-4o-mini">
                <input
                  id="ai-model"
                  type="text"
                  value={aiModel}
                  onChange={(event) => setAiModel(event.target.value)}
                  placeholder="gpt-4o-mini"
                />
              </Field>
            </div>
          </div>
        ) : null}

        <Field
          label="Extra sensitive field names (optional)"
          htmlFor="custom-fields"
          help="Comma or newline separated. Values of these fields are replaced with [REDACTED_SECRET] before anything else happens."
        >
          <input
            id="custom-fields"
            type="text"
            value={customFieldsText}
            onChange={(event) => setCustomFieldsText(event.target.value)}
            placeholder="account_number, patient_id, license_key"
          />
        </Field>

        {rememberKeyWarning ? (
          <div className="checkbox-row">
            <input
              id="acknowledge-redaction"
              type="checkbox"
              checked
              onChange={() => setRememberKeyWarning(false)}
            />
            <label htmlFor="acknowledge-redaction">
              I understand that redaction is best-effort and that I should review the sanitized text
              before sharing a report.
            </label>
          </div>
        ) : null}

        {status === 'unconfigured' ? (
          <Alert tone="warning" title="Sign-in is not configured">
            Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to enable Google sign-in.
          </Alert>
        ) : null}
      </div>
    </>
  );
}
